/**
 * useTeamCount — derives team count dynamically from fixture data.
 *
 * Counts distinct teams from the fixtures table for a given competition/season.
 * Falls back to the hardcoded CompetitionConfig.teamCount when no fixtures exist
 * (e.g., before first sync for a brand new competition).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getCompetitionById } from '@/lib/competitions';

async function fetchTeamCount(competitionId: number, season: string): Promise<number> {
  const { data, error } = await supabase
    .from('fixtures')
    .select('home_team, away_team')
    .eq('competition_id', competitionId)
    .eq('season', season);

  if (error || !data || data.length === 0) return 0;

  const teams = new Set<string>();
  for (const f of data) {
    teams.add(f.home_team);
    teams.add(f.away_team);
  }
  return teams.size;
}

export function useTeamCount(
  competitionId: number | undefined,
  season: string | undefined
): number {
  const { data: dynamicCount } = useQuery({
    queryKey: ['team-count', competitionId, season],
    queryFn: () => fetchTeamCount(competitionId!, season!),
    enabled: !!competitionId && !!season,
    staleTime: 30 * 60 * 1000, // 30 min — team count rarely changes mid-season
  });

  // Fall back to hardcoded config when no fixtures exist yet
  const fallback = competitionId ? getCompetitionById(competitionId).teamCount : 0;

  return dynamicCount || fallback;
}
